import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiMessageCircle, 
  FiPhone, 
  FiX, 
  FiUser, 
  FiMail, 
  FiSend,
  FiClock,
  FiCheckCircle
} from 'react-icons/fi';
import { bookConsultation } from '../utils/api';

const DynamicFooter = () => {
  const [showExpertModal, setShowExpertModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show when user scrolls down 300px or reaches 30% of page
      const scrollPercentage = (scrollPosition / (documentHeight - windowHeight)) * 100;
      setIsVisible(scrollPosition > 300 || scrollPercentage > 30);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone) {
      alert('Please fill in all required fields (Name, Email, Phone)');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Send consultation data to backend
      await bookConsultation({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        goal: 'weight_loss', // Default goal for expert consultations
        message: `[EXPERT CONSULTATION] ${formData.message || 'User requested to talk to an expert via dynamic footer'}`
      });
      
      setSubmitSuccess(true);
      setTimeout(() => {
        setShowExpertModal(false);
        setSubmitSuccess(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }, 2000);
    } catch (error) {
      console.error('Error submitting consultation:', error);
      // You might want to show an error message to the user here
      alert('Failed to submit consultation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const phoneNumber = process.env.REACT_APP_WHATSAPP_NUMBER || '+919355353459';
    const message = encodeURIComponent(
      "Hi! I'm interested in D-Fitclub's health and nutrition programs. Can you help me get started?"
    );
    const whatsappUrl = `https://wa.me/${phoneNumber.replace('+', '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <>
      {/* Dynamic Footer Bar */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-white shadow-2xl border-t border-gray-200"
          >
            <div className="container-max">
              <div className="flex flex-col sm:flex-row items-center justify-between py-3 px-4 space-y-3 sm:space-y-0">
                {/* Left Side - Message */}
                <div className="flex items-center space-x-3 text-center sm:text-left">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm sm:text-base">
                      Need Expert Guidance?
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Get personalized nutrition advice from our experts
                    </p>
                  </div>
                </div>

                {/* Right Side - Action Buttons */}
                <div className="flex items-center space-x-2 w-full sm:w-auto justify-center">
                  {/* Talk to Expert Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowExpertModal(true)}
                    className="bg-primary-500 text-white px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors flex items-center space-x-2 text-sm sm:text-base flex-1 sm:flex-none justify-center"
                  >
                    <FiPhone className="w-4 h-4" />
                    <span className="hidden sm:inline">Talk to Expert</span>
                    <span className="sm:hidden">Expert</span>
                  </motion.button>

                  {/* WhatsApp Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={openWhatsApp}
                    className="bg-green-500 text-white px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center space-x-2 text-sm sm:text-base flex-1 sm:flex-none justify-center"
                  >
                    <FiMessageCircle className="w-4 h-4" />
                    <span className="hidden sm:inline">WhatsApp Us</span>
                    <span className="sm:hidden">WhatsApp</span>
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expert Modal */}
      <AnimatePresence>
        {showExpertModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowExpertModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 text-white rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold mb-1">Talk to Our Expert</h3>
                    <p className="text-primary-100 text-sm">
                      Get personalized guidance for your health journey
                    </p>
                  </div>
                  <button
                    onClick={() => setShowExpertModal(false)}
                    className="text-white hover:text-primary-200 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {submitSuccess ? (
                  /* Success State */
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-8"
                  >
                    <FiCheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h4 className="text-xl font-bold text-gray-800 mb-2">
                      Request Submitted!
                    </h4>
                    <p className="text-gray-600 mb-4">
                      Our expert will contact you within 24 hours
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800 text-sm">
                        📞 Expect a call soon! Our nutrition experts are excited to help you.
                      </p>
                    </div>
                  </motion.div>
                ) : (
                  /* Form */
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Consultation Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center space-x-2 mb-2">
                        <FiClock className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-800 font-medium text-sm">
                          Free 15-minute consultation
                        </span>
                      </div>
                      <p className="text-blue-700 text-sm">
                        Our certified nutritionists will analyze your goals and recommend the best plan for you.
                      </p>
                    </div>

                    {/* Form Fields */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <div className="relative">
                        <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="input-field pl-10"
                          placeholder="Enter your full name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <div className="relative">
                        <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="input-field pl-10"
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <div className="relative">
                        <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="input-field pl-10"
                          placeholder="Enter your phone number"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tell us about your goals
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={3}
                        className="input-field resize-none"
                        placeholder="e.g., I want to lose 10kg, build muscle, improve nutrition..."
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary-500 text-white py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <FiSend className="w-4 h-4" />
                          <span>Request Free Consultation</span>
                        </>
                      )}
                    </button>

                    <p className="text-xs text-gray-500 text-center">
                      By submitting, you agree to receive calls/messages from our experts
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DynamicFooter;